import { Component, inject } from '@angular/core';
import { table, CacTableComponent, TableBulkAction, CacTableColumnSelectorComponent, TableAction, CacFieldComponent } from '@controladad/ng-base';
import { AuthStore, OrderApiService, cacTableOrderStatusCol } from '@core';
import { OrderItem } from '@models';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, finalize, switchMap, debounceTime, distinctUntilChanged } from 'rxjs';
import { formControl } from '@al00x/forms';

@Component({
  selector: 'lib-orders',
  standalone: true,
  imports: [CacTableComponent, CacTableColumnSelectorComponent, CacFieldComponent],
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss'],
})
export class OrdersComponent {
  private readonly orderApi = inject(OrderApiService);
  private readonly authStore = inject(AuthStore);
  admin = false;
  route = inject(ActivatedRoute);
  table!: any;
  searchControl = formControl('');

  constructor() {
    this.admin = this.route.snapshot.data['isAdmin'] === true;
    this.initTable();
    this.initSearchControl();
  }

  private initTable(): void {
    this.table = table({
      itemsFn: this.admin ? this.orderApi.getAdapter() : this.orderApi.vendorGetAdapter(),
      transformRequest: (request) => {
        return {
          ...request,
          filters: [
            ...(request.filters || []),
            {
              key: 'SearchQuery',
              value: this.searchControl.value,
              strictKey: false
            }
          ]
        };
      },
      selectable: true,
      view: {
        itemName: 'Order',
        title: 'Orders',
      },
      columns: {
        id: {
          label: 'Order ID',
          sortable: 'Id',
          filterable: [
            {
              type: 'contains',
              key: 'Id',
            },
          ],
        },
        customerFullName: {
          label: 'Customer Full Name',
          sortable: 'CustomerFullName',
          filterable: [
            {
              type: 'contains',
              key: 'CustomerFullName',
            },
          ],
        },
        phoneNumber: {
          label: 'Phone number',
          sortable: true,
          hide: !this.admin,
          filterable: [
            {
              type: 'contains',
              key: 'PhoneNumber',
            },
          ],
        },
        email: {
          label: 'Email',
          sortable: true,
          hide: !this.admin,
          filterable: [
            {
              type: 'contains',
              key: 'Email',
            },
          ],
        },
        purchaseDate: {
          label: 'Purchase Date',
          type: 'datetime',
          sortable: 'PurchaseDate',
          filterable: [
            { type: 'greater', label: 'From', controlType: 'date', key: 'PurchaseDateFrom' },
            { type: 'lower', label: 'To', controlType: 'date', key: 'PurchaseDateTo' },
          ],
        },
        totalPrice: {
          label: 'Total Price',
          type: 'text',
          sortable: 'TotalPrice',
          transform: (value) => `${value.toFixed(3)} KWD`,
          filterable: [
            { type: 'greater', label: 'Min Price', controlType: 'input', inputType: 'number', key: 'TotalPriceFrom' },
            { type: 'lower', label: 'Max Price', controlType: 'input', inputType: 'number', key: 'TotalPriceTo' },
          ],
        },
        status: cacTableOrderStatusCol(),
        hasPreOrderItems: {
          label: 'Pre-delivery',
          hide: !this.admin,
          type: 'tag',
          items: [{ value: true, label: 'Pre-delivery' }, { value: false, label: 'Regular' }],
          filterable: [{ type: 'equal', controlType: 'select', key: 'HasPreOrderItems',
            items: [{ value: true, label: 'Pre-delivery' }, { value: false, label: 'Regular' }] }],
        },
        isReady: {
          label: 'Ready',
          type: 'tag',
          items: [
            { value: true, label: 'Ready' },
            { value: false, label: 'Not Ready' }
          ],
        },
      },
      actions: this.getActions(),
      bulkActions: this.getBulkActions(),
      export: (filters) => this.admin ? this.orderApi.exportOrders(filters) : this.orderApi.vendorExportOrders(filters),
      events: this.admin ? {
        add: () => dialog$.addOrder({}).afterSubmit()
      } : undefined,
    });
  }

  private getActions(): TableAction<OrderItem>[] {
    if (this.admin) {
      return [
        {
          type: 'button',
          content: 'Invoice',
          appearance: 'stroked',
          action: (order: OrderItem) => {
            // First show the invoice date dialog
            return dialog$.invoiceDate({ invoiceDate: new Date(order.purchaseDate) }).afterClosed().pipe(
              switchMap((result) => {
                if (result !== null && result !== undefined) {
                  // User clicked Download (result contains { overrideDate: Date | null })
                  return this.orderApi.downloadInvoice(order.id, result.overrideDate, true).pipe(
                    switchMap(() => {
                      snackbar$.show('Invoice download started');
                      return EMPTY;
                    })
                  );
                } else {
                  // User cancelled
                  return EMPTY;
                }
              })
            );
          },
        },
        {
          type: 'button',
          content: 'Details',
          action: (order: OrderItem) => {
            return this.orderApi.getOrderDetails(order.id).pipe(
              switchMap(orderDetail => dialog$.orderDetail({ orderId: order.id, orderDetail }).afterSubmit())
            );
          },
        },
      ]
    } else {
      return [
        {
          type: 'button',
          content: 'Details',
          action: (order: OrderItem) => {
            return this.orderApi.vendorGetOrderDetails(order.id).pipe(
              switchMap(orderDetail => dialog$.vendorOrderDetail({ orderId: order.id, orderDetail }).afterSubmit())
            );
          },
        }
      ];
    }

  }

  private getBulkActions(): TableBulkAction<OrderItem>[] | undefined {
    if (!this.admin) {
      // Vendors have no bulk actions
      return undefined;
    }

    const actions: TableBulkAction<OrderItem>[] = [
      {
        content: 'Mark as Pending',
        action: (orders: OrderItem[]) =>
          dialog$
            .prompt({
              title: 'Mark Orders as Pending?',
              message: `Are you sure you want to mark ${orders.length} order(s) as Pending?`,
              yesButtonText: 'Yes, Mark as Pending',
              noButtonText: 'Cancel',
            })
            .action(() => this.orderApi.bulkChangeStatus(orders.map(o => o.id), 1)),
      },
      {
        content: 'Mark as Processing',
        action: (orders) =>
          dialog$
            .prompt({
              title: 'Mark Orders as Processing?',
              message: `Are you sure you want to mark ${orders.length} order(s) as Processing?`,
              yesButtonText: 'Yes, Mark as Processing',
              noButtonText: 'Cancel',
            })
            .action(() => this.orderApi.bulkChangeStatus(orders.map(o => o.id), 2)),
      },
      {
        content: 'Mark as Completed',
        action: (orders) =>
          dialog$
            .prompt({
              title: 'Mark Orders as Completed?',
              message: `Are you sure you want to mark ${orders.length} order(s) as Completed?`,
              yesButtonText: 'Yes, Mark as Completed',
              noButtonText: 'Cancel',
            })
            .action(() => this.orderApi.bulkChangeStatus(orders.map(o => o.id), 4)),
      },
      {
        content: 'Mark as Cancelled',
        action: (orders) =>
          dialog$
            .prompt({
              title: 'Cancel Orders?',
              message: `Are you sure you want to cancel ${orders.length} order(s)? This action cannot be undone.`,
              yesButtonText: 'Yes, Cancel Orders',
              noButtonText: 'No, Keep Orders',
            })
            .action(() => this.orderApi.bulkChangeStatus(orders.map(o => o.id), 5)),
      },
      {
        content: 'Preview Invoices',
        action: (orders, { setLoader }) => {
          const latestDate = new Date(Math.max(...orders.map(o => new Date(o.purchaseDate).getTime())));
          return dialog$.invoiceDate({ invoiceDate: latestDate }).afterClosed().pipe(
            switchMap((result) => {
              if (result !== null && result !== undefined) {
                // User clicked Download (result contains { overrideDate: Date | null })
                setLoader(true);
                return this.orderApi.bulkDownloadInvoices(orders.map(o => o.id), result.overrideDate, true)
                  .pipe(finalize(() => setLoader(false)));
              } else {
                // User cancelled
                return EMPTY;
              }
            })
          );
        }
      },
      {
        content: 'Download Invoices',
        action: (orders, { setLoader }) => {
          const latestDate = new Date(Math.max(...orders.map(o => new Date(o.purchaseDate).getTime())));
          return dialog$.invoiceDate({ invoiceDate: latestDate }).afterClosed().pipe(
            switchMap((result) => {
              if (result !== null && result !== undefined) {
                // User clicked Download (result contains { overrideDate: Date | null })
                setLoader(true);
                return this.orderApi.bulkDownloadInvoices(orders.map(o => o.id), result.overrideDate).pipe(
                  finalize(() => setLoader(false)),
                  switchMap(() => {
                    snackbar$.show('Invoices download started');
                    return EMPTY;
                  })
                );
              } else {
                // User cancelled
                return EMPTY;
              }
            })
          );
        }
      },
      ...(!this.authStore.isRestrictedAdmin() ? [{
        content: 'Delete Orders',
        action: (orders: OrderItem[]) =>
          dialog$
            .deletePrompt(true, { itemName: 'Orders' })
            .action(() => this.orderApi.delete(orders.map((o: OrderItem) => o.id))),
      }] : []),
    ];
    return actions;
  }

  private initSearchControl(): void {
    this.searchControl.value$.pipe(
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe(() => {
      this.table.ref?.refresh();
    });
  }
}
